// #599 — credential-entry page detection.
//
// WHY THIS EXISTS
// The widget initialises wherever a host mounts it, including unauthenticated
// login / signup / password-reset pages. Whether it loads on a credential page
// is purely a function of where each integrator put the <script> tag, and that
// is not a control that holds: a customer fixed exactly this in their own
// tracker, then an unrelated feature commit reinstated it the SAME DAY, and the
// widget sat on their production /login for about three months before a
// compliance audit noticed.
//
// A rule inside the widget protects every consumer, including ones that do not
// exist yet, and survives host-side regressions.
//
// WHAT THIS IS NOT
// This is not a fix for a vulnerability. The widget has no credential-capture
// capability: no DOM text/HTML serialisation, no host keystroke capture,
// screenshots only on explicit user action with password + email fields masked
// first, screen recording double-gated by our consent modal and the browser's
// own prompt, and `credentials: 'omit'` on every API call so host cookies never
// reach OnCatch. This is a defence-in-depth default about BLAST RADIUS — a
// credential page is where a compromised third-party script would do the most
// damage, the same reasoning behind PCI DSS 4.0 requirements 6.4.3 and 11.6.1
// for payment pages.
//
// WHY THE PASSWORD FIELD AND NOT THE URL
// A URL-pattern list (login, signin, register, verify, auth, mfa, otp, …) was
// considered and deliberately rejected. It silently kills the widget on
// legitimate pages — /verify-email, an app section at /auth,
// /register-for-webinar — and a silently-missing widget with no error is the
// worst support case we could create. A rendered password input IS the risk
// marker, and it carries essentially no false-positive rate.
//
// KNOWN LIMIT
// A login form inside a CROSS-ORIGIN IFRAME (hosted auth like Auth0's embedded
// widget, or a Stripe-style frame) is invisible to us — same-origin policy. We
// accept that: if the credential never touches the host page's DOM, the
// widget's presence on the parent page is materially less interesting, and no
// signal available to us can see into that frame anyway.

/** Reason a page was classified as credential-entry. Reported in telemetry. */
export type CredentialPageReason = 'password_field';

export interface CredentialPageCheck {
  isCredentialPage: boolean;
  reason: CredentialPageReason | null;
}

/**
 * True when `el` is actually laid out — not merely present in the DOM.
 *
 * THIS DISTINCTION IS LOAD-BEARING. Checking mere presence would suppress the
 * widget across an ENTIRE application whenever a password input lives in an
 * always-mounted-but-hidden node: a settings modal a SPA renders once and
 * toggles with `display:none`, a hidden change-password drawer, a
 * password-manager honeypot. Those pages are not credential-entry pages and
 * their users should keep the widget.
 *
 * `getClientRects().length` is the cheap, framework-agnostic test for "does
 * this generate a box". It is 0 for `display:none` (including any ancestor),
 * for a detached node, and for `hidden` — and non-zero for an input that is
 * merely off-screen, transparent, or covered, which is the right call: those
 * are still live inputs a reporter can type a password into.
 */
function isRendered(el: Element): boolean {
  try {
    return el.getClientRects().length > 0;
  } catch {
    // Defensive: an exotic/mocked element without the method. Treat as rendered
    // — over-suppressing is the safe direction for a privacy control.
    return true;
  }
}

/**
 * Detect whether the current document is a credential-entry page.
 *
 * Synchronous and DOM-only by design: bootstrap calls this BEFORE
 * installBuffers() and before any network call, so a suppressed page never has
 * its console/fetch/XHR patched and never reaches admin.oncatch.app.
 *
 * @param doc injectable for tests; defaults to the ambient document.
 */
export function detectCredentialPage(doc: Document = document): CredentialPageCheck {
  try {
    const fields = doc.querySelectorAll('input[type="password"]');
    for (const field of Array.from(fields)) {
      if (isRendered(field)) {
        return { isCredentialPage: true, reason: 'password_field' };
      }
    }
  } catch {
    // querySelectorAll should not throw, but a detection helper must never be
    // the thing that breaks a host page. Failing OPEN here (treating the page
    // as non-credential) matches pre-#599 behaviour rather than silently
    // removing the widget from every page on an unexpected DOM error.
    return { isCredentialPage: false, reason: null };
  }
  return { isCredentialPage: false, reason: null };
}

/**
 * Watch for a credential page appearing AFTER the widget mounted.
 *
 * In a single-page app, client-side navigation into /login does not re-run the
 * widget's boot path, so the load-time check alone would leave the widget
 * running on a credential route. This observer closes that gap.
 *
 * TEARDOWN POLICY (the open question the issue flagged, resolved here):
 * tear down unconditionally, including with the panel open and a draft typed.
 * Losing an in-progress report is regrettable; leaving the widget live on a
 * credential page because someone happened to have the panel open defeats the
 * control. The case is also rare — it requires navigating INTO a credential
 * route while mid-report — and a reporter who does lose a draft can refile from
 * the page they came from. Predictability beats a conditional here: "the widget
 * is never live on a credential page" is a claim we can make to a customer's
 * security reviewer; "usually not live" is not.
 *
 * Returns a disposer. Cheap: one MutationObserver on childList+subtree, and the
 * callback short-circuits on the same synchronous query as the load-time check.
 */
export function watchForCredentialPage(
  onDetected: (reason: CredentialPageReason) => void,
  doc: Document = document,
): () => void {
  if (typeof MutationObserver === 'undefined') return () => undefined;

  let fired = false;
  const check = (): void => {
    if (fired) return;
    const result = detectCredentialPage(doc);
    if (!result.isCredentialPage || !result.reason) return;
    fired = true;
    observer.disconnect();
    onDetected(result.reason);
  };

  const observer = new MutationObserver(check);
  try {
    observer.observe(doc.documentElement, { childList: true, subtree: true });
  } catch {
    return () => undefined;
  }

  return () => observer.disconnect();
}
